import { Button } from '@thanx/uikit/button'
import { Notification } from '@thanx/uikit/notification'
import { Text } from '@thanx/uikit/text'
import { alert } from 'actions/flash'
import {
  useAcknowledgeEscalationMutation,
  useGetConversationQuery,
  useResolveCaseMutation,
  useRestrictCustomerMutation,
  useSendConversationMessageMutation,
} from 'api/recoveryAi'
import { getErrorMessage } from 'api/utils'
import { caseDetailBadgeStatusFor } from 'components/caseDetailBadgeStatus'
import FeedbackStatusBadge from 'components/FeedbackStatusBadge'
import ArrowLeft from 'components/Icons/ArrowLeft'
import { PermissionTooltip } from 'components/Navbar/Bar/PermissionTooltip'
import RestrictCustomerModal from 'components/RestrictCustomerModal'
import SendMessageModal, {
  CustomerEmailState,
  SendMessagePayload,
} from 'components/SendMessageModal'
import TabBar, { Tab } from 'components/TabBar'
import Transcript from 'components/Transcript'
import useDispatch from 'hooks/useDispatch'
import { usePermissionReason } from 'hooks/useRbacNavCheck'
import { buildTranslate } from 'locales'
import React, { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import type { StaticContext } from 'react-router'
import { Link, RouteComponentProps } from 'react-router-dom'
import { selectCurrentMerchantUser } from 'selectors/merchantUser'
import { useStyletron } from 'styletron-react'
import { merchantTheme } from 'theme'
import { useAbility } from 'utilities/ability'
import {
  formatDate as formatDateRaw,
  formatDateTime as formatDateTimeRaw,
  formatTime as formatTimeRaw,
} from 'utilities/dateFormatters'
import CaseActionsMenu from './CaseActionsMenu'
import GrantRewardModal from './GrantRewardModal'
import OverviewTab from './OverviewTab'
import ResolveEscalationModal from './ResolveEscalationModal'

const t = buildTranslate('recovery_ai.case_detail')

// Router state carried through the /escalations/:id redirect (F3 Option A): the
// escalation id the acknowledge/resolve controls key off. Absent on a direct
// /cases/:id visit → the escalation block stays read-only.
type LocationState = { escalationId?: string }

type Props = RouteComponentProps<{ id: string }, StaticContext, LocationState>

type TabKey = 'overview' | 'transcript'

const OVERVIEW_TAB_ID = 'case-tab-overview'
const TRANSCRIPT_TAB_ID = 'case-tab-transcript'
const OVERVIEW_PANEL_ID = 'case-panel-overview'
const TRANSCRIPT_PANEL_ID = 'case-panel-transcript'

// CaseDetail's empty/invalid policy layered on the shared pure formatters
// (utilities/dateFormatters, also used by the shared Transcript): null → em-dash placeholder; an
// unparseable value → the raw string so a bad timestamp is at least visible.
const withFallback = (iso: string | null, formatted: string): string =>
  iso ? formatted || iso : t('empty')

const formatDateTime = (iso: string | null): string =>
  withFallback(iso, formatDateTimeRaw(iso))

// Time-only, for the case-timeline stepper (Figma shows "5:20 PM").
const formatTime = (iso: string | null): string =>
  withFallback(iso, formatTimeRaw(iso))

// Date-only, for the recent-activities table (Figma's "Date" column).
const formatDate = (iso: string | null): string =>
  withFallback(iso, formatDateRaw(iso))

const CaseDetail = ({ match, location }: Props) => {
  const [css] = useStyletron()
  const [activeTab, setActiveTab] = useState<TabKey>('overview')
  const [isGrantModalOpen, setIsGrantModalOpen] = useState(false)
  const [isRestrictModalOpen, setIsRestrictModalOpen] = useState(false)
  const [isSendMessageModalOpen, setIsSendMessageModalOpen] = useState(false)
  const dispatch = useDispatch()

  // The LOGGED-IN OPERATOR (not the case's customer): the resolve modal names
  // their address as where the customer's replies will land, because the BE sets
  // it as the Reply-To on the message it sends. Null until the current merchant
  // user is in the store, which hides that footer.
  const currentMerchantUser = useSelector(selectCurrentMerchantUser)
  const operatorEmail = currentMerchantUser?.email ?? null
  // currentData (not data): keyed to the current arg, so on route-change to a
  // new case ID it goes undefined while the new fetch runs — preventing the
  // detail view from briefly rendering the OLD case's data, AND ensuring the
  // loading branch handles route changes too. `data` is sticky per-cache-key
  // and would flash stale content during navigation.
  const { currentData, isFetching, isError, isUninitialized, refetch } =
    useGetConversationQuery(match.params.id)

  // The cases/:id route is view-level (no accessType), so a view-only operator
  // can reach this page — the write controls are gated in-component, mirroring
  // the sibling Order Feedback rating detail (useRatingDetail). Every action on
  // this page is customer-service work on feedback, so it gates on
  // `manage Feedback` (NOT `manage MerchantSettings`, which only configures
  // RecoveryAI itself — Aaron/Darren, Slack). Granting a reward additionally
  // spends a reward, so it also needs `manage RewardIssuance`. Hoisted above
  // the early returns to keep hook order stable.
  const ability = useAbility()
  const canManageFeedback = ability.can('manage', 'Feedback')
  const canManageRewardIssuance = ability.can('manage', 'RewardIssuance')

  // Per-button gates (RatingDetail parity): the row is always visible on an
  // open case, and each button disables per-permission rather than the whole
  // row hiding for a non-manager. usePermissionReason is a hook — called
  // unconditionally for every subject so hook order stays stable regardless of
  // which gate is failing (rules of hooks); each resolves to undefined unless
  // both the `role-based-access-control` and `rbac-permission-tooltips` flags
  // are on, in which case the buttons still disable correctly, just without a
  // tooltip.
  const feedbackPermissionDisabled = !canManageFeedback
  const rewardIssuancePermissionDisabled = !canManageRewardIssuance
  const feedbackPermissionReason = usePermissionReason(
    'Feedback',
    feedbackPermissionDisabled,
    '(view and edit)'
  )
  const rewardIssuancePermissionReason = usePermissionReason(
    'RewardIssuance',
    rewardIssuancePermissionDisabled,
    '(view and edit)'
  )
  // Mark Resolved / Restrict / Send message all gate on `manage Feedback`
  // alone. Send message resolves the case server-side now, so it must gate the
  // same as Mark Resolved — not a looser OR-gate that would let a caller resolve
  // via send without the resolve permission (review). Derived from the
  // permission boolean alone (never isResolving), so an in-flight resolve never
  // renders a misleading "missing permission" tooltip.
  // Grant Reward needs Feedback + RewardIssuance — name whichever is actually
  // missing, most-specific-cause-first, so the tooltip points at the real gap.
  const grantPermissionDisabled =
    feedbackPermissionDisabled || rewardIssuancePermissionDisabled
  const grantPermissionReason = feedbackPermissionDisabled
    ? feedbackPermissionReason
    : rewardIssuancePermissionReason

  // Drives Mark Resolved here. The Grant Reward modal calls useResolveCaseMutation
  // separately — each call site gets its own hook instance, so this state is NOT
  // shared with the modal. isResolveError drives the inline error banner; a
  // successful resolve refetches the case via invalidatesTags → getConversation
  // (currentData keeps the pane from blanking).
  const [
    resolveCase,
    { isError: isResolveError, isLoading: isResolving, reset: resetResolve },
  ] = useResolveCaseMutation()

  // The mutation state is instance-scoped and survives navigation between cases,
  // so clear any prior resolve error when the viewed case changes — a failed
  // resolve on one case must not carry its banner onto the next (review).
  // Keyed on the case id only: RTK's `reset` changes identity every time the
  // mutation fires, so listing it would re-run this right after each resolve
  // and wipe the error (same as the restrict effect below).
  useEffect(() => {
    resetResolve()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [match.params.id])

  // Send-and-resolve: emails the customer AND resolves the case server-side, so
  // it owns its own mutation instance separate from resolveCase above (same
  // reset-on-case-change rationale).
  const [
    sendConversationMessage,
    {
      isError: isSendMessageError,
      isLoading: isSendingMessage,
      error: sendMessageError,
      reset: resetSendMessage,
    },
  ] = useSendConversationMessageMutation()

  useEffect(() => {
    resetSendMessage()
    setIsSendMessageModalOpen(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- see resetResolve
  }, [match.params.id])

  // Restrict: the shared modal is presentational, so this page owns the
  // mutation. Reset on case change only; RTK's `reset` identity churns, so it
  // stays out of deps (same as useInboxActions).
  const [restrictCustomer, restrictState] = useRestrictCustomerMutation()
  useEffect(() => {
    restrictState.reset()
    setIsRestrictModalOpen(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [match.params.id])

  // Escalation acknowledge is a single fire-and-refetch button; the resolve modal
  // owns its own mutation. Acknowledge is keyed by the escalation id from router
  // state (F3). The trigger is `void`-ish here — errors surface via isAckError.
  const [
    acknowledgeEscalation,
    { isError: isAckError, isLoading: isAcking, reset: resetAck },
  ] = useAcknowledgeEscalationMutation()
  const [isResolveEscOpen, setIsResolveEscOpen] = useState(false)

  // Clear a stale acknowledge error AND close the resolve modal when the viewed
  // case changes, so neither carries across records (same rationale as resetResolve).
  useEffect(() => {
    resetAck()
    setIsResolveEscOpen(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- see resetResolve
  }, [match.params.id])

  // Hoisted above the early-return branches so React's hook-call order stays
  // stable across renders (rules-of-hooks). Static deps — TabBar's internal
  // useMemo on [tabs] keys off this reference being stable.
  const tabs: ReadonlyArray<Tab<TabKey>> = useMemo(
    () => [
      {
        key: 'overview',
        tabId: OVERVIEW_TAB_ID,
        panelId: OVERVIEW_PANEL_ID,
        label: t('tabs.overview'),
      },
      {
        key: 'transcript',
        tabId: TRANSCRIPT_TAB_ID,
        panelId: TRANSCRIPT_PANEL_ID,
        label: t('tabs.transcript'),
      },
    ],
    []
  )

  // Only blank the page when there's nothing to show: first mount
  // (isUninitialized), a route-change to a new case (currentData goes
  // undefined for the new key while it loads — see the hook comment above), or
  // a fetch that failed with nothing cached (isError && currentData
  // undefined). A BACKGROUND refetch of the same case (e.g. tag invalidation)
  // — succeeding OR failing — keeps the last data on screen so an open modal +
  // its in-flight mutation state survive instead of being torn down and
  // remounted ("modal bounce"); a background failure surfaces inline below
  // instead. currentData undefined here still prevents a "not found" flash
  // during navigation.
  if (isUninitialized || (isFetching && currentData === undefined)) {
    return (
      <div className="container py-xl">
        <Text.BodyText3 color="grey70">{t('loading')}</Text.BodyText3>
      </div>
    )
  }

  if (isError && currentData === undefined) {
    return (
      <div className="container py-xl">
        <Text.BodyText3 color="grey70">{t('error')}</Text.BodyText3>
        <div className={css({ marginTop: '12px' })}>
          <Button kind="secondary" onClick={() => refetch()}>
            {t('retry')}
          </Button>
        </div>
      </div>
    )
  }

  if (!currentData) {
    return (
      <div className="container py-xl">
        <Text.BodyText3 color="grey70">{t('not_found')}</Text.BodyText3>
      </div>
    )
  }

  const data = currentData

  // Write actions apply only to an open case — once resolved we don't offer
  // re-resolving / re-granting (review: avoid a double-resolve).
  const isCaseClosed =
    data.status === 'resolved' || data.status === 'manually_resolved'

  // The customer may already be restricted from a DIFFERENT case (restricting
  // doesn't necessarily close every one of their cases) — this case can still be
  // open while the customer is already on the restricted list.
  const isAlreadyRestricted = data.customerRestricted

  // Escalation actions gate on the ESCALATION's own status (independent of the
  // case's isCaseClosed) and require the escalation id — present only when arrived
  // via the escalation email/queue redirect (F3). `escalationId` is read with
  // optional chaining because router state is absent on a direct /cases/:id
  // visit or a refresh.
  const escalationId = location.state?.escalationId
  const escalationStatus = data.escalation?.status
  // VISIBILITY is a pure DATA gate, not a permission one (main-row parity):
  // the row stays visible whenever there's a real, unresolved escalation to act
  // on, and disables per-button instead of hiding for a viewer who lacks a
  // permission.
  const hasActionableEscalation =
    !!data.escalation && escalationStatus !== 'resolved'
  // Resolving/acknowledging an escalation IS responding to feedback (it writes a
  // resolution), so it gates on `manage Feedback` — the same gate Mark Resolved
  // / Restrict / Send message above use.
  const escalationActionsPermissionDisabled = feedbackPermissionDisabled
  const escalationActionsPermissionReason = feedbackPermissionReason

  // The customer's display name for the grant modal's heading ("Grant reward to
  // …"), joined the way the customer-profile page's getFullName does. Empty →
  // null, so a conversation carrying neither name falls back to the modal's
  // generic heading instead of "Grant reward to ".
  const customerName =
    [data.customer.firstName, data.customer.lastName]
      .filter(Boolean)
      .join(' ') || null

  // Mark Resolved resolves directly with no confirmation — fire-and-forget (no
  // .unwrap()), so errors surface through isResolveError's banner below and a
  // success refetches the case through resolveCase's tag invalidation. No
  // message is sent (see Send message for the send-and-resolve action instead).
  const handleMarkResolved = () =>
    resolveCase({ id: data.id, resolution_type: 'resolved_without_reward' })

  // Send-and-resolve — `.unwrap()` so a failure rejects and the modal stays open
  // on its own error banner instead of closing over a write that didn't happen.
  // On success the case resolves server-side and drops off the queue; the flash
  // confirms the SEND specifically (delivery is async and the resolve is the
  // more visible of the two effects), so the operator doesn't re-send and fire a
  // duplicate customer email.
  const handleSendMessage = (payload: SendMessagePayload) =>
    sendConversationMessage({ id: data.id, ...payload })
      .unwrap()
      .then(() => {
        dispatch(
          alert({
            key: 'success',
            message: t('send_message.success'),
            timeout: 5,
          })
        )
      })

  // Only `absent`/`loading` block the send; a hidden (PII-gated) address on an
  // existing customer is `masked` and still delivers server-side. A conversation
  // always has a resolvable recipient, so a null email here is masked, never
  // absent (see SendMessageModal's CustomerEmailState).
  const customerEmailState: CustomerEmailState = data.customer.email
    ? { status: 'known', value: data.customer.email }
    : { status: 'masked' }

  // Prefer the backend's specific 422 copy (e.g. a guard message) over the
  // generic fallback, so "please try again" never masks an actionable reason.
  const sendMessageErrorMessage = sendMessageError
    ? getErrorMessage(sendMessageError, t('send_message.error'))
    : t('send_message.error')

  const activeTabId =
    activeTab === 'overview' ? OVERVIEW_TAB_ID : TRANSCRIPT_TAB_ID
  const activePanelId =
    activeTab === 'overview' ? OVERVIEW_PANEL_ID : TRANSCRIPT_PANEL_ID

  return (
    <div className="container py-xl">
      {/* Header */}
      <Link
        to="/recovery_ai/escalations"
        className={`d-flex align-items-center ${css({
          width: 'fit-content',
          // Dark, not the default link blue (RatingDetailHeader parity,
          // AI-1024). The arrow inherits this via currentColor.
          color: merchantTheme.colors.grey90,
          fontWeight: 700,
          textDecoration: 'none',
          gap: '8px',
          ':hover': {
            color: merchantTheme.colors.grey90,
            textDecoration: 'none',
          },
        })}`}
      >
        {/* Decorative — the link text already conveys the action. */}
        <span aria-hidden="true" className={css({ display: 'flex' })}>
          <ArrowLeft />
        </span>
        {t('back')}
      </Link>

      <div
        className={`d-flex align-items-start justify-content-between ${css({
          marginTop: '12px',
          marginBottom: '24px',
          gap: '16px',
        })}`}
      >
        <div>
          {/* Stacked badge → title → date subtitle (RatingDetailHeader parity,
              AI-1024): the badge maps the wire status onto the shared
              order-rating vocabulary (escalated reads as unresolved,
              manually_resolved reads as resolved) via
              caseDetailBadgeStatusFor, rendered non-bold/uppercase via
              variant="order-rating". */}
          <div className={css({ marginBottom: '8px' })}>
            <FeedbackStatusBadge
              status={caseDetailBadgeStatusFor(data.status)}
              variant="order-rating"
            />
          </div>
          <Text.Header3>
            {/* Human order number (DATA-4857 producer-blocked nil today → em-dash). */}
            {t('order_heading', {
              number: data.order?.orderNumber ?? t('empty'),
            })}
          </Text.Header3>
          {/* The ORDER's date/time (not the case/conversation's), mirroring
              the order-rating detail's subtitle — formatDateTime already
              renders the em-dash fallback (t('empty')) for a null timestamp,
              same policy as the title's order number above. */}
          <Text.BodyText4 color="grey70">
            {formatDateTime(data.order?.orderDate ?? null)}
          </Text.BodyText4>
        </div>
        {/* Operator actions — always visible on an open case (RatingDetail
            parity); each button carries its own permission-disabled gate
            (manage Feedback, plus RewardIssuance for Grant) rather than the
            whole row hiding for a non-manager. CaseActionsMenu's "..." overflow
            trigger holds the sole destructive action (Restrict customer),
            pulled out of the button row per design; Grant Reward opens the
            reward modal; Send message opens the send-and-resolve modal; Mark
            Resolved resolves the case without a reward — all three gate on
            manage Feedback. */}
        {!isCaseClosed && (
          <div className={`d-flex align-items-center ${css({ gap: '8px' })}`}>
            <CaseActionsMenu
              permissionDisabled={feedbackPermissionDisabled}
              permissionReason={feedbackPermissionReason}
              isAlreadyRestricted={isAlreadyRestricted}
              isResolving={isResolving}
              onRestrictClick={() => setIsRestrictModalOpen(true)}
            />
            {/* `inline` on every wrapper: PermissionTooltip defaults its
                target span to `d-block w-100`, and inside this flex row that
                makes every button a full-width flex item — they compete for
                the same 100%, get shrunk, and longer labels wrap onto two
                lines. Inline keeps each button its natural width, so a
                disabled button looks exactly like an enabled one. */}
            <PermissionTooltip
              disabled={grantPermissionDisabled}
              reason={grantPermissionReason}
              id="case-detail-grant-reward-perm"
              placement="top"
              inline
            >
              <Button
                kind="secondary"
                onClick={() => setIsGrantModalOpen(true)}
                disabled={isResolving || grantPermissionDisabled}
              >
                {t('actions.grant_reward')}
              </Button>
            </PermissionTooltip>
            <PermissionTooltip
              disabled={feedbackPermissionDisabled}
              reason={feedbackPermissionReason}
              id="case-detail-send-message-perm"
              placement="top"
              inline
            >
              <Button
                kind="secondary"
                onClick={() => setIsSendMessageModalOpen(true)}
                disabled={isResolving || feedbackPermissionDisabled}
              >
                {t('actions.send_message')}
              </Button>
            </PermissionTooltip>
            <PermissionTooltip
              disabled={feedbackPermissionDisabled}
              reason={feedbackPermissionReason}
              id="case-detail-mark-resolved-perm"
              placement="top"
              inline
            >
              <Button
                kind="primary"
                onClick={handleMarkResolved}
                disabled={isResolving || feedbackPermissionDisabled}
              >
                {t('actions.mark_resolved')}
              </Button>
            </PermissionTooltip>
          </div>
        )}
      </div>

      {/* Escalation operator actions (F3/F4): a distinct group from the case
          actions above, gated on the escalation's own status + the escalation id
          from router state. Acknowledge (pending only) fires inline; Resolve opens
          the modal. Each button carries its own permission-disabled gate
          (manage Feedback, main-row parity) rather than the row hiding
          for a viewer who lacks it. */}
      {hasActionableEscalation && escalationId && (
        <div
          className={`d-flex align-items-center ${css({
            marginBottom: '24px',
            gap: '8px',
          })}`}
        >
          <Text.BodyText4 bold color="grey90">
            {t('escalation.actions_heading')}
          </Text.BodyText4>
          {escalationStatus === 'pending' && (
            <PermissionTooltip
              disabled={escalationActionsPermissionDisabled}
              reason={escalationActionsPermissionReason}
              id="case-detail-escalation-acknowledge-perm"
              placement="top"
              inline
            >
              <Button
                kind="secondary"
                onClick={() => acknowledgeEscalation({ id: escalationId })}
                disabled={isAcking || escalationActionsPermissionDisabled}
                aria-label={t('escalation.acknowledge_aria')}
              >
                {t('escalation.acknowledge')}
              </Button>
            </PermissionTooltip>
          )}
          <PermissionTooltip
            disabled={escalationActionsPermissionDisabled}
            reason={escalationActionsPermissionReason}
            id="case-detail-escalation-resolve-perm"
            placement="top"
            inline
          >
            <Button
              kind="secondary"
              onClick={() => setIsResolveEscOpen(true)}
              disabled={escalationActionsPermissionDisabled}
              aria-label={t('escalation.resolve_aria')}
            >
              {t('escalation.resolve')}
            </Button>
          </PermissionTooltip>
        </div>
      )}

      {/* A background refetch failing over still-good currentData (see the
          loading-gate comment above) — closeable without a reset(): isError is
          a QUERY flag with no manual reset, and it clears itself on the next
          successful fetch. */}
      {isError && (
        <div className={css({ marginBottom: '24px' })}>
          <Notification kind="negative" size="full" closeable>
            {t('refetch_error')}
          </Notification>
        </div>
      )}

      {isAckError && (
        <div className={css({ marginBottom: '24px' })}>
          <Notification
            kind="negative"
            size="full"
            closeable
            onClose={resetAck}
          >
            {t('escalation.acknowledge_error')}
          </Notification>
        </div>
      )}

      {/* No page-level banner for a send-only failure (unlike isResolveError
          above): Mark Resolved fires resolveCase directly with no modal open,
          but sending always goes through SendMessageModal, which already
          renders isSendMessageError inline — a page-level banner here would
          double the same error. */}
      {isResolveError && (
        <div className={css({ marginBottom: '24px' })}>
          <Notification
            kind="negative"
            size="full"
            closeable
            onClose={resetResolve}
          >
            {t('resolve.error')}
          </Notification>
        </div>
      )}

      {/* Tabs */}
      <div className={css({ marginBottom: '24px' })}>
        <TabBar
          tabs={tabs}
          activeTab={activeTab}
          onTabChange={setActiveTab}
          ariaLabel={t('tabs_label')}
        />
      </div>

      <div
        role="tabpanel"
        id={activePanelId}
        aria-labelledby={activeTabId}
        tabIndex={0}
      >
        {activeTab === 'overview' ? (
          <OverviewTab
            conversation={data}
            formatDateTime={formatDateTime}
            formatTime={formatTime}
            formatDate={formatDate}
          />
        ) : (
          <Transcript messages={data.messages} />
        )}
      </div>

      {!isCaseClosed && !grantPermissionDisabled && (
        <GrantRewardModal
          isOpen={isGrantModalOpen}
          conversationId={data.id}
          customerName={customerName}
          onClose={() => setIsGrantModalOpen(false)}
        />
      )}

      {/* Send-and-resolve: emails the customer AND resolves the case
          server-side (resolvesRecord drives the "Send and resolve" button +
          the resolve note in the modal). Gated on manage Feedback, matching the
          trigger button above; kept to open cases only (the mockup depicts just
          the open-case action bar). */}
      {!isCaseClosed && !feedbackPermissionDisabled && (
        <SendMessageModal
          isOpen={isSendMessageModalOpen}
          customerEmail={customerEmailState}
          customerName={customerName}
          resolvesRecord
          operatorEmail={operatorEmail}
          onSend={handleSendMessage}
          isSubmitting={isSendingMessage}
          isError={isSendMessageError}
          onResetError={resetSendMessage}
          errorMessage={sendMessageErrorMessage}
          onClose={() => setIsSendMessageModalOpen(false)}
        />
      )}

      {/* A successful restrict refetches the case to manually_resolved via the
          mutation's tag invalidation → isCaseClosed flips true and this whole
          action/modal block unmounts (the case leaves the queue). */}
      {!isCaseClosed && !feedbackPermissionDisabled && (
        <RestrictCustomerModal
          isOpen={isRestrictModalOpen}
          conversationId={data.id}
          otherOpenEscalationsCount={data.otherOpenEscalationsCount}
          otherOpenEscalationIds={data.otherOpenEscalationIds}
          customerName={data.customer.name}
          onConfirm={params => restrictCustomer(params).unwrap()}
          isSubmitting={restrictState.isLoading}
          isError={restrictState.isError}
          error={restrictState.error}
          onResetError={restrictState.reset}
          onClose={() => setIsRestrictModalOpen(false)}
        />
      )}

      {/* Resolve-escalation modal — mounted only when an escalation id is present
          (arrived via the escalation redirect) AND the viewer actually holds
          escalationActionsPermissionDisabled's permissions; owns the resolve
          mutation. The disabled trigger can't open it anyway, but this guard is
          the one that actually stops an unauthorized viewer, by any route
          (RatingDetail / CaseDetail's own GrantRewardModal parity). */}
      {hasActionableEscalation &&
        escalationId &&
        !escalationActionsPermissionDisabled && (
          <ResolveEscalationModal
            isOpen={isResolveEscOpen}
            escalationId={escalationId}
            onClose={() => setIsResolveEscOpen(false)}
          />
        )}
    </div>
  )
}

export default CaseDetail
